import { router } from 'expo-router';
import {
  AlertCircle,
  Check,
  RefreshCw,
} from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Text,
  View,
} from 'react-native';

import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Stack } from '@/components/Stack';
import { colors } from '@/constants/theme';
import { cashoutStore } from '@/stores/cashout.store';

import { styles } from './status-styles';

export function ProcessingView({
  amountCents,
  cashoutId,
}: {
  amountCents?: number;
  cashoutId: number;
}) {
  const isTimedOut = cashoutStore.isTimedOut;

  return (
    <Screen scroll={false} edges={['top', 'bottom']} testID="cashout-processing">
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.deepGreen} />
        {amountCents != null && (
          <MoneyDisplay cents={amountCents} size="base" />
        )}
        <Heading size="2xl">
          {isTimedOut ? 'Still processing...' : 'Generating your reward...'}
        </Heading>
        <Text style={styles.caption}>
          {isTimedOut
            ? 'This is taking longer than usual.'
            : 'This usually takes a few seconds'}
        </Text>
        {isTimedOut && (
          <Button
            label="Refresh"
            variant="outline"
            leadingIcon={RefreshCw}
            onPress={() => void cashoutStore.refreshStatus(cashoutId)}
            accessibilityLabel="Refresh cashout status"
            testID="cashout-refresh"
          />
        )}
      </View>
    </Screen>
  );
}

export function ReadyView({ amountCents }: { amountCents: number }) {
  const scale = useRef(new Animated.Value(0.6)).current;

  useEffect(() => {
    Animated.spring(scale, {
      toValue: 1,
      damping: 12,
      stiffness: 200,
      useNativeDriver: true,
    }).start();
  }, [scale]);

  const handleDone = () => {
    cashoutStore.reset();
    router.replace('/(tabs)');
  };

  return (
    <Screen scroll={false} edges={['top', 'bottom']} testID="cashout-ready">
      <View style={styles.centered}>
        <Animated.View
          style={[styles.checkCircle, { transform: [{ scale }] }]}
        >
          <Check size={48} color={colors.deepGreen} strokeWidth={2.5} />
        </Animated.View>
        <Overline>YOU CASHED OUT</Overline>
        <MoneyDisplay cents={amountCents} size="hero" showCents />
        <Text style={styles.readyBody}>
          Check your email 📩 to redeem. Choose Visa, Amazon, Venmo, or PayPal.
        </Text>
        <Stack gap="sm" style={styles.ctaStack}>
          <Button
            label="Done"
            variant="ghost"
            fullWidth
            onPress={handleDone}
            accessibilityLabel="Return to home screen"
            testID="cashout-done"
          />
        </Stack>
      </View>
    </Screen>
  );
}

export function FailedView({
  amountCents,
  failureReason,
}: {
  amountCents: number;
  failureReason: string | null;
}) {
  const handleRetry = () => {
    cashoutStore.reset();
    router.replace('/cashout');
  };

  const handleGoHome = () => {
    cashoutStore.reset();
    router.replace('/(tabs)');
  };

  return (
    <Screen scroll={false} edges={['top', 'bottom']} testID="cashout-failed">
      <View style={styles.centered}>
        <AlertCircle size={48} color={colors.dangerBright} />
        <Heading size="2xl">Something went wrong</Heading>
        <Text style={styles.failedReason}>
          {failureReason || "We couldn't process your cashout. Please try again."}
        </Text>
        <Text style={styles.caption}>
          Your balance has been restored. No funds were deducted.
        </Text>
        <Stack gap="sm" style={styles.ctaStack}>
          <Button
            label="Try again"
            variant="primary"
            fullWidth
            onPress={handleRetry}
            accessibilityLabel="Try cashing out again"
            testID="cashout-retry"
          />
          <Button
            label="Go home"
            variant="ghost"
            fullWidth
            onPress={handleGoHome}
            accessibilityLabel="Return to home screen"
            testID="cashout-go-home"
          />
        </Stack>
      </View>
    </Screen>
  );
}

